import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Typography,
} from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";
import { apisPath } from "../../utils/path";
import { AuthContext } from "../../context/AuthContext";
import { CommonContext } from "../../context/CommonContext";
import { authHttp } from "../../services/commonHttpService";
import StaffResourcesSection from "./StaffResourcesSection/StaffResourcesSection";
import DoctorsListSection from "./DoctorsListSection/DoctorsListSection";
import AddDoctorModal from "./AddDoctorModal/AddDoctorModal";

const HrManagement = () => {
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [allStaffData, setAllStaffData] = useState([]); // Store all data (doctors + nurses)
  const [doctorCardData, setDoctorCardData] = useState({});
  const [selectedView, setSelectedView] = useState("doctors"); // 'doctors' or 'nurses'
  const { setIsLoading, setAlertState } = useContext(CommonContext);
  const { facilityId } = useContext(AuthContext);

  useEffect(() => {
    getDoctorData();
    getDoctorsCardDetails();
  }, []);

  const getDoctorsCardDetails = async () => {
    setIsLoading(true);

    try {
      const response = await authHttp.get(apisPath?.getDoctorsCardDetails);

      if (response?.data?.success) {
        setDoctorCardData(response?.data?.result);
      }
    } catch (error) {
      setAlertState({
        open: true,
        title:
          error?.response?.data?.message,
        btnTxt: "CLOSE",
        status: "fail",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDoctorData = async () => {
    setIsLoading(true);

    try {
      const response = await authHttp.get(`${apisPath?.getDoctorDetails}/${facilityId}`);

      if (response?.data?.status || response?.data?.data) {
        setAllStaffData(response?.data?.data);
      }
    } catch (error) {
      setAlertState({
        open: true,
        title:
          error?.response?.data?.message || "Failed to fetch staff details",
        btnTxt: "CLOSE",
        status: "fail",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Delete doctor/nurse - actual deletion logic
  const performDelete = async (doctorId) => {
    setIsLoading(true);

    try {
      const response = await authHttp.delete(`${apisPath?.doctorDelete}/${doctorId}`);

      if (response?.data?.success || response?.data?.data) {
        setAlertState({
          open: true,
          title:
            response?.data?.message,
          btnTxt: "CLOSE",
          status: "success",
        });
        getDoctorData();
        getDoctorsCardDetails();
      }
    } catch (error) {
      setAlertState({
        open: true,
        title:
          error?.response?.data?.message || "Failed to delete staff details",
        btnTxt: "CLOSE",
        status: "fail",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete with confirmation
  const handleDelete = (doctorId) => {
    const entityName = selectedView === "doctors" ? "Doctor" : "Nurse";

    setAlertState((prev) => ({
      ...prev,
      open: true,
      message: `Are you sure you want to delete the ${entityName}'s detail?`,
      btnTxt: "Yes",
      cancelBtnTxt: "No",
      status: "warning",
      type: "confirm",
      onConfirm: () => {
        performDelete(doctorId);
      },
      onClose: null,
    }));
  };

  // Handle view change (doctors/nurses)
  const handleViewChange = (view) => {
    setSelectedView(view);
  };

  // Filter data based on selected view
  const filteredData = allStaffData.filter((item) => {
    if (selectedView === "doctors") {
      return item?.type === "Doctor";
    } else {
      return item?.type === "Nurse";
    }
  });

  return (
    <Container>
      {/* Page Title */}
      <Typography variant="h6" fontWeight={600} mb={3}>
        Facility HR Management
      </Typography>

      {/* Staff & Resources Section */}
      <StaffResourcesSection
        doctorCardData={doctorCardData}
        selectedView={selectedView}
        onViewChange={handleViewChange}
      />

      {/* Doctors List Section */}
      <DoctorsListSection
        doctorData={filteredData}
        selectedView={selectedView}
        onAddDoctor={() => setDoctorOpen(true)}
        onDeleteDoctor={handleDelete}
      />

      {/* Add Doctor Modal */}
      <AddDoctorModal
        open={doctorOpen}
        selectedView={selectedView}
        onClose={() => setDoctorOpen(false)}
        getDoctorData={getDoctorData}
        getDoctorsCardDetails={getDoctorsCardDetails}
      />
    </Container>
  );
};

export default HrManagement;
